import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import {
  managerConnectorService,
  type GatewayTopFailure,
} from './api/connector-gateways.service';

interface Props {
  gatewayId: string;
  limit?: number;
}

/**
 * P3.2 (issue #62): mostra "top falhas" agregadas por (handler, errorClass)
 * num gateway específico. Considera apenas DLQs não-resolvidas.
 *
 * Suporte usa isso pra direcionar foco — sem precisar do cliente.
 */
export function GatewayTopFailures({ gatewayId, limit = 5 }: Props) {
  const query = useQuery({
    queryKey: ['gateway-top-failures', gatewayId, limit],
    queryFn: () => managerConnectorService.getTopFailures(gatewayId, limit),
    enabled: !!gatewayId,
    refetchInterval: 30_000,
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-3 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
        <h4 className="text-sm font-semibold text-gray-900">Top falhas (DLQ não-resolvida)</h4>
      </div>

      {query.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : query.isError ? (
        <p className="text-sm text-red-600">Falha ao carregar telemetria.</p>
      ) : !query.data?.length ? (
        <p className="text-sm text-gray-500">Nenhuma falha não-resolvida 🎉</p>
      ) : (
        <ul className="space-y-1.5">
          {query.data.map((f, idx) => (
            <FailureRow key={`${f.handlerName}-${f.errorClass}-${idx}`} failure={f} />
          ))}
        </ul>
      )}
    </div>
  );
}

function FailureRow({ failure }: { failure: GatewayTopFailure }) {
  const lastFailed = new Date(failure.lastFailedAt).toLocaleString('pt-BR');
  return (
    <li className="flex items-start justify-between gap-3 rounded-lg bg-gray-50/60 px-3 py-2">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-sm font-medium text-gray-900">
            {failure.handlerName ?? <span className="text-gray-400">—</span>}
          </span>
          <span className="font-mono text-xs text-amber-700">{failure.errorClass}</span>
        </div>
        <p className="mt-0.5 text-xs text-gray-500">Última: {lastFailed}</p>
      </div>
      <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
        {failure.count}×
      </span>
    </li>
  );
}
